import { useSession } from './session';

/**
 * The company a page with a picker should open on, and how to remember a new choice.
 *
 * Every one of these pages defaulted to `companies[0]` — the first alphabetically — which is
 * the right company for exactly one person: whoever works on the one at the top of the list.
 * Everybody else re-picked theirs on arriving at each page, every time.
 *
 * THE FALLBACK ORDER IS DELIBERATE and it never fails to an empty selection while the person
 * has any company at all: the remembered one, then the first. The server has already checked
 * the remembered id against their memberships and sent null if it no longer resolves, so a
 * deleted company or an access change lands on the first company rather than on a blank page
 * or a 404.
 *
 * REMEMBERING GOES THROUGH THE SESSION, which updates its own copy before it writes to the
 * server — see `rememberCompany`. That is what makes a choice on one page the default on the
 * next one without a refresh: every page reads this from the same session object, so the
 * server write is a background detail rather than the thing the screen is waiting on.
 */
export function useRememberedCompany(available?: Array<{ id: string }>): {
  companyId: string;
  remember: (id: string) => void;
} {
  const { session, rememberCompany } = useSession();
  /**
   * The list the PAGE offers, which is not always every company the person belongs to:
   * Changes lists only those they may merge in. Resolving against the page's own list is what
   * stops a remembered company the page cannot show from selecting nothing at all.
   */
  const companies = available ?? session?.companies ?? [];
  const remembered = session?.person.lastCompanyId ?? null;

  const companyId =
    (remembered && companies.some((c) => c.id === remembered) ? remembered : null) ??
    companies[0]?.id ??
    '';

  return { companyId, remember: rememberCompany };
}
